import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CaretLeft, CaretRight, X } from '@phosphor-icons/react';

/**
 * Modal plein écran affichant un diaporama des photos design d'une commande.
 * - Navigation flèches (bouton + touches ← →)
 * - Swipe gauche/droite sur mobile (tactile)
 * - Escape ferme le modal
 * - `getImageUrl(imgId)` doit retourner l'URL publique via token
 *
 * Utilisé côté client dans Viewer3D après clic sur « Voir les photos ».
 */
export default function DiaporamaModal({ open, onClose, images, getImageUrl }) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);

  useEffect(() => { if (open) setIndex(0); }, [open]);

  const next = useCallback(() => setIndex((i) => (i + 1) % images.length), [images.length]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + images.length) % images.length), [images.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, next, prev]);

  if (!open || !images?.length) return null;

  const current = images[index];

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) { dx < 0 ? next() : prev(); }
    touchStartX.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="diaporama-modal"
    >
      {/* Header : compteur + fermer */}
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="flex items-center gap-3">
          <div className="text-sm font-medium" data-testid="diaporama-counter">
            {index + 1} / {images.length}
          </div>
          {current?.filename && <span className="text-zinc-400 text-xs" data-testid="diaporama-filename">{current.filename}</span>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-sm hover:bg-white/10 transition-colors"
          aria-label="Fermer le diaporama"
          data-testid="diaporama-close"
        >
          <X size={20} />
        </button>
      </div>

      {/* Image + navigation */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        {images.length > 1 && (
          <button
            type="button"
            onClick={prev}
            className="absolute left-2 sm:left-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Photo précédente"
            data-testid="diaporama-prev"
          >
            <CaretLeft size={24} />
          </button>
        )}

        <img
          src={getImageUrl(current.id)}
          alt={current.filename || `Photo ${index + 1}`}
          className="max-h-full max-w-full object-contain select-none"
          draggable={false}
          data-testid="diaporama-image"
        />

        {images.length > 1 && (
          <button
            type="button"
            onClick={next}
            className="absolute right-2 sm:right-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Photo suivante"
            data-testid="diaporama-next"
          >
            <CaretRight size={24} />
          </button>
        )}
      </div>

      {/* Thumbnails strip */}
      {images.length > 1 && (
        <div className="px-4 py-3 overflow-x-auto flex gap-2 justify-center bg-black/40" data-testid="diaporama-thumbs">
          {images.map((img, i) => (
            <button
              key={img.id}
              type="button"
              onClick={() => setIndex(i)}
              className={`flex-shrink-0 w-14 h-14 rounded-sm overflow-hidden border-2 transition-all ${i === index ? 'border-white opacity-100' : 'border-transparent opacity-60 hover:opacity-90'}`}
              aria-label={`Photo ${i + 1}`}
              data-testid={`diaporama-thumb-btn-${i}`}
            >
              <img src={getImageUrl(img.id)} alt={img.filename || ''} className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
